import { useEffect, useState } from 'react'
import cx from 'classnames'
import Styles from './V4ContentRecommendations.module.scss'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { Text } from 'blaze-ui/Text/Text'
import { Heading } from 'blaze-ui/Heading/Heading'
import { TopBar } from '../TopBar/TopBar'
import { V4StepFooter } from '../V4StepFooter'
import { useStepAction } from '../useStepAction'
import { useBrandPlanSchedule } from 'hooks/queries/schedules/useBrandPlanSchedule'
import { useUpdateBrandPlanSchedule } from 'hooks/queries/schedules/useUpdateBrandPlanSchedule'
import { useBrandPlan } from 'hooks/queries/brandPlans/useBrandPlan'
import { useUpdateBrandPlan } from 'hooks/queries/useUpdateBrandPlan'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useCategoryCreditCosts } from 'hooks/useCategoryCreditCosts'
import { useRootStore } from 'stores/RootStore'
import {
  ContentCategory,
  CONTENT_CATEGORIES,
  CONTENT_CATEGORY_THUMBNAILS,
  ContentTypeCounts,
} from 'utils/brandPlans/contentCategories'
import { BrandPlanContentStrategies, ContentCount } from 'types'
import {
  deriveInitialCountsFromStrategy,
  buildContentsFromCounts,
} from 'screens/ContentPlan/CreateCampaignModal/contentTypeDefaultsUtils'
import { ChevronDown, ChevronUp, Credits } from 'blaze-ui/icons/20'

const CARD_COPY: Record<ContentCategory, { label: string; subtitle: string }> = {
  still_image_post: { label: 'Still Images', subtitle: 'Single image post for feeds' },
  carousel_post: { label: 'Carousels', subtitle: 'Multi-slide storytelling' },
  feed_video_post: { label: 'Feed Videos', subtitle: 'Polished video for feed' },
  short_form_video: { label: 'Short-form Video', subtitle: 'Reels, TikToks, Shorts' },
  stories: { label: 'Stories', subtitle: 'Ephemeral vertical content' },
  blog_post: { label: 'Blogs', subtitle: 'Long-form SEO articles' },
  newsletter: { label: 'Emails', subtitle: 'Newsletters and campaigns' },
}

// ---- Sub-components ----

interface ContentRecommendationCardProps {
  category: ContentCategory
  count: number
  cost: number
  isEditing: boolean
  onCountChange: (category: ContentCategory, delta: number) => void
}

const ContentRecommendationCard = ({
  category,
  count,
  cost,
  isEditing,
  onCountChange,
}: ContentRecommendationCardProps) => {
  const copy = CARD_COPY[category]
  const thumbnail = CONTENT_CATEGORY_THUMBNAILS[category]
  return (
    <div className={cx(Styles.card, { [Styles.cardZero]: count === 0 })}>
      <div className={Styles.thumbnail}>
        <img className={Styles.thumbnailImage} src={thumbnail} alt={copy.label} />
      </div>
      <div className={Styles.cardInfo}>
        <div className={Styles.cardTextBlock}>
          <span className={Styles.cardTitle}>{copy.label}</span>
          <span className={Styles.cardSubtitle}>{copy.subtitle}</span>
        </div>
        <div className={Styles.creditsLabel}>
          <Credits size={15} color="var(--dark-40)" />
          <span className={Styles.creditsText}>{cost} credits each</span>
        </div>
      </div>
      <div className={Styles.countRow}>
        {isEditing ? (
          <div className={Styles.counterInput}>
            <input
              type="text"
              readOnly
              className={cx(Styles.counterInputField, { [Styles.counterZero]: count === 0 })}
              value={count}
            />
            <button
              type="button"
              className={cx(Styles.counterButton, Styles.decrementButton)}
              onClick={() => onCountChange(category, -1)}
              disabled={count <= 0}
            >
              −
            </button>
            <button
              type="button"
              className={cx(Styles.counterButton, Styles.incrementButton)}
              onClick={() => onCountChange(category, 1)}
            >
              +
            </button>
          </div>
        ) : (
          <>
            <span className={Styles.countNumber}>{count}</span>
            <span className={Styles.countUnit}>/ week</span>
          </>
        )}
      </div>
    </div>
  )
}

interface ContentCreditsSidebarProps {
  totalCredits: number
  trialCredits: number
  breakdownItems: { category: ContentCategory; label: string; count: number; cost: number; total: number }[]
  isEditing: boolean
  onToggleEditing: () => void
}

const ContentCreditsSidebar = ({
  totalCredits,
  trialCredits,
  breakdownItems,
  isEditing,
  onToggleEditing,
}: ContentCreditsSidebarProps) => {
  const [breakdownOpen, setBreakdownOpen] = useState(false)
  return (
    <aside className={Styles.sidebar}>
      <p className={Styles.sidebarKicker}>This amount uses</p>
      <div className={Styles.creditSummary}>
        <span className={Styles.creditNumber}>{totalCredits}</span>
        <span className={Styles.creditUnit}>credits / week</span>
      </div>
      <p className={Styles.sidebarBody}>
        Credits are what Blaze spends to generate your content. Each type costs a different amount.
      </p>

      <div className={Styles.accordionSection}>
        <button type="button" className={Styles.accordionToggle} onClick={() => setBreakdownOpen((prev) => !prev)}>
          <span>How costs break down</span>
          {breakdownOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>

        {breakdownOpen && (
          <div className={Styles.breakdownList}>
            <hr className={Styles.breakdownDivider} />
            {breakdownItems.map((item) => (
              <div key={item.category} className={Styles.breakdownRow}>
                <span className={Styles.breakdownLabel}>{item.label}</span>
                <span className={Styles.breakdownCalc}>
                  {item.count} x {item.cost} credits
                </span>
                <span className={Styles.breakdownTotal}>{item.total}</span>
              </div>
            ))}
            <div className={Styles.breakdownSummaryRow}>
              <span className={Styles.breakdownSummaryLabel}>Total credits per week:</span>
              <span className={Styles.breakdownSummaryValue}>{totalCredits}</span>
            </div>
          </div>
        )}
      </div>

      <hr className={Styles.sidebarDivider} />
      <p className={Styles.sidebarFooter}>
        Your trial includes {trialCredits} credits — enough to generate your first week.
      </p>

      {/* <button type="button" className={Styles.adjustButton} onClick={onToggleEditing}>
        {isEditing ? (
          <>
            <Check02 size={15} />
            Done adjusting
          </>
        ) : (
          <>
            <Pencil02 size={15} />
            Adjust quantities
          </>
        )}
      </button> */}
    </aside>
  )
}

function mergeContentsIntoContentStrategies(
  existing: BrandPlanContentStrategies | null | undefined,
  updates: Record<string, ContentCount[] | undefined>,
): BrandPlanContentStrategies | undefined {
  if (!existing) return undefined
  const strategyKey = Object.keys(existing)[0]
  if (!strategyKey) return undefined
  return {
    ...existing,
    [strategyKey]: {
      ...existing[strategyKey as keyof typeof existing],
      ...updates,
    },
  }
}

// ---- Main component ----

interface Props {
  scheduleId?: number
  brandPlanId?: number
  onNext: () => void
  onBack: () => void
}

export const V4ContentRecommendations = ({ scheduleId, brandPlanId, onNext, onBack }: Props) => {
  const { isMobile } = useResponsiveDesign()
  const brandPlanOnly = scheduleId == null
  const { data: schedule, isLoading: isLoadingSchedule } = useBrandPlanSchedule(scheduleId ?? 0, {
    enabled: !brandPlanOnly,
  })
  const { data: brandPlanData, isLoading: isLoadingBrandPlan } = useBrandPlan(brandPlanId ?? 0, {
    enabled: brandPlanId != null && brandPlanId > 0,
  })
  const { mutateAsync: updateSchedule } = useUpdateBrandPlanSchedule({ id: scheduleId ?? 0 })
  const { mutateAsync: updateBrandPlan } = useUpdateBrandPlan({ id: brandPlanId ?? 0 })
  const isLoading = brandPlanOnly ? isLoadingBrandPlan : isLoadingSchedule
  const dataSource = brandPlanOnly ? brandPlanData : schedule
  const categoryCosts = useCategoryCreditCosts()
  const {
    creditUsageStore: { creditUsage },
  } = useRootStore()
  const trialCredits = creditUsage?.base_credits_available ?? 0

  const [initialized, setInitialized] = useState(false)
  const [counts, setCounts] = useState<ContentTypeCounts>({
    still_image_post: 0,
    carousel_post: 0,
    feed_video_post: 0,
    short_form_video: 0,
    stories: 0,
    blog_post: 0,
    newsletter: 0,
  })
  const [isEditing, setIsEditing] = useState(false)
  const { isSaving, run } = useStepAction()

  useEffect(() => {
    if (!dataSource || initialized) return
    setInitialized(true)
    const derived = deriveInitialCountsFromStrategy(dataSource)
    const fbStoryCount = (dataSource.facebook_contents ?? [])
      .filter((c) => c.type === 'story')
      .reduce((s, c) => s + (c.count ?? 0), 0)
    const initial = { ...derived, stories: derived.stories + fbStoryCount }
    setCounts(initial)
  }, [dataSource, initialized])

  const handleCountChange = (category: ContentCategory, delta: number) => {
    setCounts((prev) => ({
      ...prev,
      [category]: Math.max(0, prev[category] + delta),
    }))
  }

  const totalCredits = CONTENT_CATEGORIES.reduce((sum, category) => {
    const cost = categoryCosts[category] ?? 0
    return sum + counts[category] * cost
  }, 0)

  const breakdownItems = CONTENT_CATEGORIES.filter((cat) => counts[cat] > 0).map((category) => {
    const cost = categoryCosts[category] ?? 0
    return { category, label: CARD_COPY[category].label, count: counts[category], cost, total: counts[category] * cost }
  })

  const handleNext = () =>
    run(async () => {
      if (!dataSource) return
      const contents: ReturnType<typeof buildContentsFromCounts> & { facebook_contents?: ContentCount[] } =
        buildContentsFromCounts(counts)
      const origFbStory = (dataSource.facebook_contents ?? []).find((c) => c.type === 'story')
      const origIgStory = (dataSource.instagram_contents ?? []).find((c) => c.type === 'story')
      const hasFbStory = (origFbStory?.count ?? 0) > 0
      const hasIgStory = (origIgStory?.count ?? 0) > 0

      if (hasFbStory) {
        const fbStoryCount = hasIgStory ? Math.floor(counts.stories / 2) : counts.stories
        const igStoryCount = counts.stories - fbStoryCount
        contents.instagram_contents = igStoryCount > 0 ? [{ type: 'story', count: igStoryCount }] : []
        contents.facebook_contents = fbStoryCount > 0 ? [{ type: 'story', count: fbStoryCount }] : []
      }

      if (brandPlanOnly) {
        await updateBrandPlan({ ...contents, tactics: dataSource.tactics ?? [] })
      } else {
        await updateSchedule({ ...contents, tactics: dataSource.tactics ?? [], update_all: true })
      }

      const strategyContentUpdates: Record<string, ContentCount[]> = {
        crosspost_contents: contents.crosspost_contents,
        video_crosspost_contents: contents.video_crosspost_contents,
        seo_contents: contents.seo_contents,
        email_contents: contents.email_contents,
        instagram_contents: contents.instagram_contents,
      }
      if (contents.facebook_contents) {
        strategyContentUpdates.facebook_contents = contents.facebook_contents
      }
      const updatedStrategies = mergeContentsIntoContentStrategies(
        brandPlanData?.content_strategies,
        strategyContentUpdates,
      )
      if (updatedStrategies) {
        await updateBrandPlan({ content_strategies: updatedStrategies })
      }

      onNext()
    }, 'Something went wrong saving your content plan. Please try again.')

  if (isLoading || isLoadingBrandPlan || !dataSource || !initialized) return null

  return (
    <MultiStepStepLayout headingLevel={isMobile ? 2 : 1} scrollContainerClassName={Styles.scrollContainer}>
      <V4StepFooter onBack={onBack} onNext={handleNext} isSaving={isSaving} nextLabel="Start with this amount">
        <div className={Styles.content}>
          <TopBar highlightStep="campaign" />

          <div className={Styles.headerSection}>
            <div className={Styles.heading}>
              <Heading level={isMobile ? 2 : 1}>Here&apos;s what we recommend for your first week</Heading>
            </div>
            <Text color="var(--dark-60)">
              Based on your strategy and channels, this is a good starting mix for a business like yours. You can change
              these anytime after setup.
            </Text>
          </div>

          <div className={Styles.mainRow}>
            <div className={Styles.left}>
              <div className={Styles.grid}>
                {CONTENT_CATEGORIES.map((category) => (
                  <ContentRecommendationCard
                    key={category}
                    category={category}
                    count={counts[category]}
                    cost={categoryCosts[category] ?? 0}
                    isEditing={isEditing}
                    onCountChange={handleCountChange}
                  />
                ))}
              </div>
            </div>

            {!isMobile && (
              <ContentCreditsSidebar
                totalCredits={totalCredits}
                trialCredits={trialCredits}
                breakdownItems={breakdownItems}
                isEditing={isEditing}
                onToggleEditing={() => setIsEditing((prev) => !prev)}
              />
            )}
          </div>
        </div>
      </V4StepFooter>
    </MultiStepStepLayout>
  )
}
